import type {
  IncomingInviteDto,
  MatchResultDto,
  MatchStateDto,
  MeDto,
  RoundResultDto,
  SideDto,
  TournamentDto,
  TurnDto,
  ZoneDto,
} from '@penalty-cup/shared';

/**
 * Состояние игры на клиенте и чистые обработчики событий сервера (тестируются без сокета).
 * Источник истины — сервер: клиент только показывает присланное и копит ввод текущего удара.
 */

/** starting — входим; outside — не в Telegram; denied — сервер не принял initData; offline — нет связи. */
export type Session = 'starting' | 'outside' | 'denied' | 'offline' | 'ready';

/** Ввод удара: зона → сила → точность → отправлено. Вратарю — только зона. */
export type InputStage = 'zone' | 'power' | 'accuracy' | 'sent';

export interface GameData {
  session: Session;
  token: string | null;
  me: MeDto | null;
  tournament: TournamentDto | null;
  match: MatchStateDto | null;
  turn: TurnDto | null;
  stage: InputStage;
  draft: { zone?: ZoneDto; power?: number };
  /** последний раскрытый удар — для анимации и плашки */
  lastKick: RoundResultDto | null;
  /** итог матча — экран победы/поражения, пока не начнётся следующий матч или игрок не закроет */
  matchResult: MatchResultDto | null;
  /** поправка к часам: серверное время ≈ Date.now() + clockOffset */
  clockOffset: number;
  joining: boolean;
  /** соединение с игрой потеряно (сокет переподключается) */
  reconnecting: boolean;
  /** шаг онбординга после сохранения профиля (тренировка); null — онбординг не идёт */
  onboardingStep: 'trainShot' | 'trainSave' | null;
  /** открытый раздел с главного экрана */
  view: 'leaderboard' | 'shop' | null;
  /** открыли по ссылке «играть» — встать в турнир, как только можно (после онбординга) */
  autoPlay: boolean;
  /** открыли по ссылке-приглашению (duel_/cup_) — принять после онбординга */
  pendingInvite: string | null;
  sharing: 'idle' | 'busy' | 'failed';
  /** ошибка последнего действия (PLAY и т. п.) — показывается на экране */
  notice: string | null;
  soundOn: boolean;
  /** матч кончился, но последний удар ещё досматривается — экран итога чуть позже */
  finalReveal: boolean;
  /** вызов от друга, пришедший в открытую игру — окно «Принять / Отклонить» */
  incomingInvite: IncomingInviteDto | null;
}

export const initialGame: GameData = {
  session: 'starting',
  token: null,
  me: null,
  tournament: null,
  match: null,
  turn: null,
  stage: 'zone',
  draft: {},
  lastKick: null,
  matchResult: null,
  clockOffset: 0,
  joining: false,
  reconnecting: false,
  onboardingStep: null,
  view: null,
  autoPlay: false,
  pendingInvite: null,
  sharing: 'idle',
  notice: null,
  soundOn: true,
  finalReveal: false,
  incomingInvite: null,
};

export type Screen =
  | 'splash'
  | 'onboarding'
  | 'home'
  | 'leaderboard'
  | 'shop'
  | 'lobby'
  | 'search'
  | 'bracket'
  | 'match'
  | 'matchEnd'
  | 'out'
  | 'finish';

export function screenOf(s: GameData): Screen {
  if (s.session !== 'ready') return 'splash';
  // Сначала онбординг: имя команды и форма, затем тренировка (матч и турнир — важнее, если уже идут)
  if (!s.match && !s.matchResult && (s.onboardingStep || (s.me && !s.me.teamName))) return 'onboarding';
  if (s.matchResult)
    return s.finalReveal && s.match?.matchId === s.matchResult.matchId ? 'match' : 'matchEnd';
  if (s.match && s.match.phase !== 'FINISHED') return 'match';
  const t = s.tournament;
  if (!t) return s.view ?? 'home';
  if (t.status === 'WAITING') return t.kind === 'CUP' ? 'search' : 'lobby';
  if (t.status === 'FINISHED') return 'finish';
  if (t.status === 'CANCELLED') return 'home';
  const you = t.players.find((p) => p.isYou);
  if (you?.eliminatedIn) return 'out';
  return 'bracket';
}

export const myRole = (s: GameData): 'shooter' | 'keeper' | null =>
  s.turn && s.match ? (s.turn.shooter === s.match.you ? 'shooter' : 'keeper') : null;

const offsetFrom = (serverTime: number) => serverTime - Date.now();

// ---------- события сервера ----------

export function onMatchState(s: GameData, m: MatchStateDto): Partial<GameData> {
  const sameMatch = s.match?.matchId === m.matchId;
  const turn = m.turn;
  return {
    match: m,
    turn,
    stage: turn?.submitted ? 'sent' : 'zone',
    draft: turn && sameMatch && s.turn?.roundId === turn.roundId && !turn.submitted ? s.draft : {},
    // Новый матч турнира — экран итога прошлого закрываем сами, иначе игрок пропустит ходы
    matchResult: sameMatch ? (m.result ?? s.matchResult) : m.phase === 'FINISHED' ? m.result : null,
    lastKick: sameMatch ? s.lastKick : null,
    finalReveal: sameMatch ? s.finalReveal : false,
    ...(turn ? { clockOffset: offsetFrom(turn.serverTime) } : {}),
  };
}

export function onRoundStart(s: GameData, turn: TurnDto): Partial<GameData> {
  if (!s.match || s.match.matchId !== turn.matchId) return {};
  return {
    turn,
    stage: turn.submitted ? 'sent' : 'zone',
    draft: {},
    match: { ...s.match, phase: 'PLAYING' },
    clockOffset: offsetFrom(turn.serverTime),
  };
}

export function onRoundResult(s: GameData, r: RoundResultDto): Partial<GameData> {
  if (!s.match || s.match.matchId !== r.matchId) return {};
  const kicks = s.match.kicks.some((k) => k.seq === r.kick.seq) ? s.match.kicks : [...s.match.kicks, r.kick];
  return { match: { ...s.match, kicks, score: r.score }, lastKick: r, turn: null, stage: 'sent' };
}

export function onMatchFinished(s: GameData, r: MatchResultDto): Partial<GameData> {
  if (!s.match || s.match.matchId !== r.matchId) return {};
  return { matchResult: r, match: { ...s.match, phase: 'FINISHED', result: r, score: r.score }, turn: null };
}

export function onTournament(_s: GameData, t: TournamentDto): Partial<GameData> {
  return { tournament: t, clockOffset: offsetFrom(t.serverTime) };
}

export const youWon = (s: GameData): boolean => !!s.matchResult && s.matchResult.winner === s.match?.you;
export const otherSide = (side: SideDto): SideDto => (side === 'A' ? 'B' : 'A');
